import {
  type TNavbarItemElement,
  type TNavbarMenuBurgerContentElement,
  type TNavbarMenuBurgerElement,
  type TNavbarMenuElement,
  type TNavbarMenuListElement,
  type TNavbarMenuListGroupElement,
  getNavbarUserButtonContent,
} from '@/navbarElement';
import type { NavbarV2SerializableNode } from '@/navbarElementV2/navbarMenuElement';
import type { InlineNode } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { deepClone } from './deepClone';

const getNavbarItemTextContainer = <T>(text: string, type: string) => {
  return {
    type,
    content: [
      {
        type: 'text',
        text,
      },
    ],
  } as unknown as T;
};

export const constructMobileView = (root: TNavbarMenuElement) => {
  if (root.type !== 'navbar_menu') return root;

  // Cast root to the correct type for better type inference
  const navbarMenu = deepClone(root as TNavbarMenuElement);

  const { content, ...rest } = navbarMenu;

  // TODO: fix unknown type here
  const listGroups = content?.flatMap((c) => c.content as unknown).filter(Boolean) as TNavbarMenuListGroupElement[];
  const items = listGroups?.flatMap((c) => c.content).filter(Boolean) as TNavbarItemElement[];

  // Set parentInMobile to true for all dropdown elements that are children of a navbar_item that has showOnMobile set to true
  for (const item of items) {
    if (
      (item.attrs?.type === 'dropdown' || (item.attrs?.type === 'user_button' && item.attrs?.style === 'dropdown')) &&
      item.content
    ) {
      for (const navbarItemContent of item.content) {
        if (navbarItemContent?.attrs) {
          navbarItemContent.attrs.parentInMobile = item.attrs?.showOnMobile === true;
        }
      }
    }
  }

  const showLeftMobile = items.filter(
    (c) => c.attrs?.showOnMobile && c.attrs?.mobileJustify === 'start',
  ) as TNavbarItemElement[];
  const showRightMobile = items.filter(
    (c) => c.attrs?.showOnMobile && (c.attrs?.mobileJustify === 'end' || !c.attrs?.mobileJustify),
  ) as TNavbarItemElement[];
  const showCenterMobile = items.filter(
    (c) => c.attrs?.showOnMobile && c.attrs?.mobileJustify === 'center',
  ) as TNavbarItemElement[];
  const mobileItems = items
    .filter((c) => !c.attrs?.showOnMobile)
    .sort((a, b) => {
      // Sorting rules:
      if (a.attrs?.type === 'search') return -1;
      if (b.attrs?.type === 'search') return 1;

      const aAction = a.attrs?.action;
      const bAction = b.attrs?.action;
      if (aAction === 'sign_up') return 1;
      if (bAction === 'sign_up') return -1;
      if (aAction === 'login') return 1;
      if (bAction === 'login') return -1;
      if (aAction === 'profile') return 1;
      if (bAction === 'profile') return -1;
      return 0;
    }) as TNavbarItemElement[];

  // burgerDropdownBackground
  const burgerContent: TNavbarMenuBurgerContentElement = {
    type: 'navbar_menu_burger_content',
    attrs: {
      id: uuidv4(),
    },
    content: mobileItems,
  };
  const burgerJustify = navbarMenu.attrs?.burgerJustify || 'end';
  const burger: TNavbarMenuBurgerElement = {
    type: 'navbar_menu_burger',
    attrs: {
      id: uuidv4(),
      mobileJustify: burgerJustify,
      color: navbarMenu.attrs?.burgerColor,
      contentBackgroundColor: navbarMenu.attrs?.burgerDropdownBackground,
    },
    content: [burgerContent],
  };

  const listGroupLeft: TNavbarMenuListGroupElement = {
    type: 'navbar_menu_list_group',
    attrs: {
      ...listGroups?.[0]?.attrs,
      id: listGroups?.[0]?.attrs?.id || uuidv4(),
      type: 'left',
    },
    content: burgerJustify === 'start' ? [burger, ...showLeftMobile] : showLeftMobile,
  };
  const listGroupCenter: TNavbarMenuListGroupElement = {
    type: 'navbar_menu_list_group',
    attrs: {
      ...listGroups?.[1]?.attrs,
      id: listGroups?.[1]?.attrs?.id || uuidv4(),
      type: 'middle',
    },
    content: showCenterMobile,
  };

  const listGroupRight: TNavbarMenuListGroupElement = {
    type: 'navbar_menu_list_group',
    attrs: {
      ...listGroups?.[2]?.attrs,
      id: listGroups?.[2]?.attrs?.id || uuidv4(),
      type: 'right',
    },
    content: burgerJustify === 'end' ? [...showRightMobile, burger] : showRightMobile,
  };
  const addedMobileListMenu: TNavbarMenuListElement = {
    type: 'navbar_menu_list',
    attrs: {
      ...content?.[0]?.attrs,
      id: content?.[0]?.attrs?.id || uuidv4(),
      justify: 'center',
      height: 68,
    },
    content: [listGroupLeft, listGroupCenter, listGroupRight],
  };

  return {
    ...rest,
    content: [addedMobileListMenu],
  };
};

export const navbarV2Migrator = <T extends NavbarV2SerializableNode | InlineNode = NavbarV2SerializableNode>(
  navbar: T,
): NavbarV2SerializableNode => {
  const hydrateElements = (node: NavbarV2SerializableNode): void => {
    if ('text' in node || node.type === 'hardBreak') return;

    node.content?.forEach(hydrateElements);

    switch (node.type) {
      case 'navbar_menu': {
        if (node.content && node.attrs?.rows === 1) {
          const lists = node.content.filter((c) => c.type === 'navbar_menu_list');
          if (lists.length === 2) {
            node.content = [
              ...node.content.filter((c) => c.type !== 'navbar_menu_list'),
              lists[0], // keep only the first
            ];
          }
        }
        break;
      }
      case 'navbar_item': {
        switch (node.attrs?.type) {
          case 'search':
          case 'logo':
            break;
          case 'user_button':
            if (node.attrs.showWhen === 'all_logged_in' || (!node.attrs.showWhen && node.attrs.action === 'profile')) {
              if (!node.content || (Array.isArray(node?.content) && node.content.length === 0)) {
                const userButtonContent = getNavbarUserButtonContent();
                hydrateElements(userButtonContent);
                node.content = [userButtonContent];
              }
              node.attrs.showWhen = 'all_logged_in';
              node.attrs.style = 'dropdown';
            } else {
              node.content = [getNavbarItemTextContainer(node.attrs.label || '', 'navbar_item_text_container')];
              node.attrs.style = 'button';
            }
            break;

          default: {
            if (!node.attrs?.label) break;

            const labelText = node.attrs?.label || '';

            const textAlreadyExists = node.content?.some((item) => item.type === 'navbar_item_text_container');

            if (textAlreadyExists) break;

            if (node.attrs?.type === 'button' || node.attrs?.type === 'link') {
              node.content = [getNavbarItemTextContainer(labelText, 'navbar_item_text_container')];
            } else if (node.content) {
              node.content.unshift(getNavbarItemTextContainer(labelText, 'navbar_item_text_container'));
            } else {
              // Fallback, should not happen
              node.content = [getNavbarItemTextContainer(labelText, 'navbar_item_text_container')];
            }

            node.attrs = {
              ...node.attrs,
              showOnMobile: node.attrs?.showOnMobile === false ? undefined : node.attrs?.showOnMobile,
            };

            break;
          }
        }
        break;
      }

      case 'navbar_menu_list': {
        // Extract line divider properties from parent
        const lineDividerProps = {
          lineDividerStyle: node.attrs?.lineDividerStyle,
          lineDividerWidth: node.attrs?.lineDividerWidth,
          lineDividerColor: node.attrs?.lineDividerColor,
          lineDividerHeight: node.attrs?.lineDividerHeight || '18px',
        };

        // Apply to all child groups
        node.content?.forEach((group) => {
          if (group.type === 'navbar_menu_list_group' && group.attrs) {
            group.attrs = {
              ...group.attrs,
              ...lineDividerProps,
            };
          }
        });

        // Migrate justify value to middle group
        const justifyValue = node.attrs?.justify;
        if (justifyValue) {
          const middleGroup = node.content?.find(
            (group) => group.type === 'navbar_menu_list_group' && group.attrs?.type === 'middle',
          ) as any;
          if (middleGroup?.attrs) {
            middleGroup.attrs.justify = justifyValue;
          }
        }

        // Remove line divider properties and justify from parent (keep X-axis ones)
        if (node.attrs) {
          const {
            lineDividerStyle: _lineDividerStyle,
            lineDividerWidth: _lineDividerWidth,
            lineDividerColor: _lineDividerColor,
            lineDividerHeight: _lineDividerHeight,
            justify: _justify,
            ...restAttrs
          } = node.attrs;
          node.attrs = restAttrs;
        }
        break;
      }

      // case 'navbar_menu':
      // case 'navbar_dropdown':
      // case 'navbar_menu_list_group':
      // case 'navbar_dropdown_column':
      // case 'navbar_menu_burger_content':
      // case 'navbar_menu_list':
      case 'navbar_dropdown_item': {
        if (!node.content) node.content = [];

        const attrs = node.attrs;

        if (attrs?.description && !node.content.some((child) => child.type === 'navbar_dropdown_item_description')) {
          node.content.unshift(getNavbarItemTextContainer(attrs.description, 'navbar_dropdown_item_description'));
        }

        if (attrs?.title && !node.content.some((child) => child.type === 'navbar_dropdown_item_title')) {
          node.content.unshift(getNavbarItemTextContainer(attrs.title, 'navbar_dropdown_item_title'));
        }

        break;
      }
      // case 'navbar_menu_burger':
      default:
        break;
    }
  };

  const result = deepClone(navbar as NavbarV2SerializableNode);

  hydrateElements(result);

  return result;
};
